import { Injectable } from '@angular/core';

/**
 * DataPathService - Serviço de Manipulação de Data Paths
 * 
 * Centraliza toda a lógica de manipulação de paths de dados que antes estava
 * espalhada entre DataSyncService, DataGridComponent, MasterDetailPanel, etc.
 * 
 * Responsabilidades:
 * - Parsing e validação de paths (ex: 'response.item_list', 'response.magalu.channels')
 * - Construção de paths aninhados com índices
 * - Resolução de paths relativos a partir de seleção pai
 * - Utilitários para manipulação de paths
 * 
 * IMPORTANTE: Este serviço NÃO é global (providedIn: 'root')
 * Ele deve ser fornecido a nível de componente para garantir
 * instâncias isoladas por launcher.
 */
@Injectable()
export class DataPathService {
  
  /** Cache de paths parseados para evitar re-parsing */
  private pathPartsCache = new Map<string, string[]>();
  
  constructor() {}

  // ============================================================================
  // Parsing e Validação
  // ============================================================================

  /**
   * Divide um path em suas partes (com cache para performance)
   * @param path Path a ser parseado (ex: 'response.item_list')
   * @returns Array de partes do path
   */
  parsePath(path: string): string[] {
    if (!path) return [];
    
    // Usar cache se disponível
    if (this.pathPartsCache.has(path)) {
      return this.pathPartsCache.get(path)!;
    }
    
    const parts = path.split('.');
    this.pathPartsCache.set(path, parts);
    return parts;
  }

  /**
   * Retorna a profundidade de um path (número de partes)
   * @param path Path a analisar
   * @returns Número de partes no path
   */
  getPathDepth(path: string): number {
    return this.parsePath(path).length;
  }

  /**
   * Verifica se um path é válido (não vazio e tem pelo menos 2 partes)
   * @param path Path a validar
   * @returns true se o path é válido
   */
  isValidPath(path: string): boolean {
    if (!path || typeof path !== 'string') return false;
    const parts = this.parsePath(path);
    return parts.length >= 2; // Mínimo: 'response.xxx'
  }

  /**
   * Verifica se um path é de uma grade aninhada (profundidade > 2)
   * Ex: 'response.item_list' = false (profundidade 2)
   * Ex: 'response.magalu.channels' = true (profundidade 3)
   * @param path Path a verificar
   * @returns true se é um path de grade aninhada
   */
  isNestedPath(path: string): boolean {
    return this.getPathDepth(path) > 2;
  }

  /**
   * Verifica se um path é de grade principal (profundidade === 2)
   * @param path Path a verificar
   * @returns true se é um path de grade principal
   */
  isMainGridPath(path: string): boolean {
    return this.getPathDepth(path) === 2;
  }

  // ============================================================================
  // Construção de Paths
  // ============================================================================

  /**
   * Constrói um path completo com índice
   * Ex: buildIndexedPath('response.item_list', 0) => 'response.item_list[0]'
   * @param arrayPath Path do array
   * @param index Índice do item
   * @returns Path com índice
   */
  buildIndexedPath(arrayPath: string, index: number): string {
    if (!arrayPath || index < 0) return '';
    return `${arrayPath}[${index}]`;
  }

  /**
   * Constrói um path completo para uma propriedade dentro de um item de array
   * Ex: buildPropertyPath('response.item_list', 0, 'name') => 'response.item_list[0].name'
   * @param arrayPath Path do array
   * @param index Índice do item
   * @param propertyPath Path da propriedade dentro do item
   * @returns Path completo da propriedade
   */
  buildPropertyPath(arrayPath: string, index: number, propertyPath: string): string {
    const indexedPath = this.buildIndexedPath(arrayPath, index);
    if (!indexedPath || !propertyPath) return indexedPath;
    return `${indexedPath}.${propertyPath}`;
  }

  /**
   * Constrói um path para grade aninhada a partir da seleção pai
   * Ex: buildNestedArrayPath('response.magalu', 0, 'channels') => 'response.magalu[0].channels'
   * @param parentArrayPath Path do array pai
   * @param parentIndex Índice do item pai selecionado
   * @param nestedArrayName Nome do array aninhado dentro do item pai
   * @returns Path completo do array aninhado
   */
  buildNestedArrayPath(parentArrayPath: string, parentIndex: number, nestedArrayName: string): string {
    const parentIndexedPath = this.buildIndexedPath(parentArrayPath, parentIndex);
    if (!parentIndexedPath || !nestedArrayName) return '';
    return `${parentIndexedPath}.${nestedArrayName}`;
  }

  /**
   * Constrói um path completo para edição de célula em grade aninhada
   * Ex: buildNestedCellPath('response.magalu', 0, 'channels', 2, 'name')
   *     => 'response.magalu[0].channels[2].name'
   * @param parentArrayPath Path do array pai
   * @param parentIndex Índice do item pai
   * @param nestedArrayName Nome do array aninhado
   * @param nestedIndex Índice do item no array aninhado
   * @param columnPath Path da coluna
   * @returns Path completo para a célula
   */
  buildNestedCellPath(
    parentArrayPath: string,
    parentIndex: number,
    nestedArrayName: string,
    nestedIndex: number,
    columnPath: string
  ): string {
    const nestedArrayPath = this.buildNestedArrayPath(parentArrayPath, parentIndex, nestedArrayName);
    if (!nestedArrayPath) return '';
    return this.buildPropertyPath(nestedArrayPath, nestedIndex, columnPath);
  }

  // ============================================================================
  // Extração e Decomposição de Paths
  // ============================================================================

  /**
   * Extrai o path pai de um path aninhado
   * Ex: getParentPath('response.magalu.channels') => 'response.magalu'
   * @param path Path completo
   * @returns Path pai ou string vazia se não houver
   */
  getParentPath(path: string): string {
    const parts = this.parsePath(path);
    if (parts.length <= 2) return ''; // Já é path de primeiro nível
    return parts.slice(0, -1).join('.');
  }

  /**
   * Extrai o nome do array de um path
   * Ex: getArrayName('response.magalu.channels') => 'channels'
   * @param path Path completo
   * @returns Nome do array (última parte do path)
   */
  getArrayName(path: string): string {
    const parts = this.parsePath(path);
    return parts.length > 0 ? parts[parts.length - 1] : '';
  }

  /**
   * Extrai a parte aninhada de um path relativo a um path pai
   * Ex: getNestedPart('response.magalu.channels', 'response.magalu') => 'channels'
   * @param fullPath Path completo
   * @param parentPath Path pai
   * @returns Parte aninhada do path
   */
  getNestedPart(fullPath: string, parentPath: string): string {
    if (!fullPath.startsWith(parentPath)) return fullPath;
    const nested = fullPath.replace(parentPath + '.', '');
    return nested;
  }

  /**
   * Extrai índice de um path indexado
   * Ex: extractIndex('response.item_list[5]') => 5
   * @param indexedPath Path com índice
   * @returns Índice extraído ou -1 se não encontrado
   */
  extractIndex(indexedPath: string): number {
    const match = indexedPath.match(/\[(\d+)\]$/);
    return match ? parseInt(match[1], 10) : -1;
  }

  /**
   * Remove o índice de um path indexado
   * Ex: removeIndex('response.item_list[5]') => 'response.item_list'
   * @param indexedPath Path com índice
   * @returns Path sem índice
   */
  removeIndex(indexedPath: string): string {
    return indexedPath.replace(/\[\d+\]$/, '');
  }

  // ============================================================================
  // Comparação e Validação de Paths
  // ============================================================================

  /**
   * Verifica se dois paths são equivalentes
   * @param path1 Primeiro path
   * @param path2 Segundo path
   * @returns true se são equivalentes
   */
  pathsEqual(path1: string, path2: string): boolean {
    if (!path1 && !path2) return true;
    if (!path1 || !path2) return false;
    return path1 === path2;
  }

  /**
   * Verifica se um path é descendente de outro (está dentro dele)
   * Ex: isDescendantOf('response.magalu.channels', 'response.magalu') => true
   * @param childPath Path filho candidato
   * @param parentPath Path pai candidato
   * @returns true se childPath é descendente de parentPath
   */
  isDescendantOf(childPath: string, parentPath: string): boolean {
    if (!childPath || !parentPath) return false;
    return childPath.startsWith(parentPath + '.');
  }

  /**
   * Calcula a profundidade relativa entre dois paths
   * Ex: getRelativeDepth('response.magalu.channels', 'response.magalu') => 1
   * @param childPath Path mais profundo
   * @param parentPath Path mais raso
   * @returns Diferença de profundidade ou -1 se não são relacionados
   */
  getRelativeDepth(childPath: string, parentPath: string): number {
    if (!this.isDescendantOf(childPath, parentPath)) return -1;
    return this.getPathDepth(childPath) - this.getPathDepth(parentPath);
  }

  // ============================================================================
  // Utilitários
  // ============================================================================

  /**
   * Limpa o cache de paths (útil em reset ou troca de tela)
   */
  clearCache(): void {
    this.pathPartsCache.clear();
  }

  /**
   * Junta múltiplas partes em um path
   * Ex: joinPath('response', 'item_list', 'channels') => 'response.item_list.channels'
   * @param parts Partes do path
   * @returns Path completo
   */
  joinPath(...parts: string[]): string {
    return parts.filter(p => p && p.length > 0).join('.');
  }

  /**
   * Normaliza um path (remove espaços, índices vazios, etc.)
   * @param path Path a normalizar
   * @returns Path normalizado
   */
  normalizePath(path: string): string {
    if (!path) return '';
    // Remove espaços e normaliza separadores
    return path.trim().replace(/\s+/g, '').replace(/\.+/g, '.');
  }
}
